import React, { useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { Field, Form } from 'react-final-form';
import moment from 'moment';

import CircularProgress from '@material-ui/core/CircularProgress';
import Download from '@material-ui/icons/GetApp';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import makeStyles from '@material-ui/core/styles/makeStyles';

import ActionButton from 'components/buttons/ActionButton';
import { Dialog } from 'components/common';
import { Input } from 'components/form';
import FilterDateInput from 'components/inputs/FilterDateInput';
import { number, composeValidators, maxValue } from 'components/form/validationRules';
import { NumberFormat, numberInputFormatter } from 'services/helpers/inputFormatters';

let intervalId;

const initialValues = { minSpend: '', maxRecords: '', date: { from: null, to: null } };

const useStyles = makeStyles((theme) => ({
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
}));

const decodeDateParam = (dateParam) => decodeURIComponent(dateParam);

const ExportTopSpendersButton = (props) => {
  const classes = useStyles();

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleExportDebtors = async ({ minSpend, maxRecords, date }) => {
    try {
      setLoading(true);
      const params: any = {};
      if (minSpend) params.minSpend = minSpend;
      if (maxRecords) params.maxRecords = maxRecords;
      if (date?.from) params.startDate = decodeDateParam(date.from);
      if (date?.to) params.endDate = decodeDateParam(date.to);

      const {
        data: { executionArn },
      } = await dataProvider.get('exportTopSpenders', params);

      intervalId = setInterval(async () => {
        const {
          data: { status, downloadURL },
        } = await dataProvider.get('exportReportStatus', {
          arn: executionArn,
        });
        if (status !== 'RUNNING') {
          setLoading(false);
          clearInterval(intervalId);
          if (status === 'SUCCEEDED' && downloadURL) {
            handleCloseDialog();
            const data: any = await fetch(downloadURL);
            const url = window.URL.createObjectURL(new Blob([await data.text()]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Top_Spenders_Report_${moment().format()}.csv`);
            document.body.appendChild(link);

            link.click();
            link.parentNode.removeChild(link);
          }
        }
      }, 5000);
    } catch (error) {
      notify(error.message);
      setLoading(false);
    }
  };

  return (
    <>
      <ActionButton
        disabled={loading}
        onClick={handleOpenDialog}
        classes={{
          label: classes.button,
        }}>
        Export Top Spenders {loading ? <CircularProgress size={20} /> : <Download />}
      </ActionButton>
      {openDialog && (
        <Form
          onSubmit={handleExportDebtors}
          initialValues={initialValues}
          render={({ handleSubmit, submitting }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Dialog
                  open={openDialog}
                  onCancel={handleCloseDialog}
                  title="Export Top Spenders"
                  onConfirm={handleSubmit}
                  confirmText="Export"
                  submitting={submitting || loading}>
                  <Input
                    name="minSpend"
                    label="Minimum Spend"
                    validate={composeValidators(number, maxValue(1000000))}
                    formatValue={numberInputFormatter[NumberFormat.Integer]}
                  />
                  <Input
                    name="maxRecords"
                    label="Maximum Records"
                    validate={composeValidators(number, maxValue(1000000))}
                    formatValue={numberInputFormatter[NumberFormat.Integer]}
                  />
                  <Field
                    name="date"
                    label="Date"
                    render={(inputProps) => (
                      <FilterDateInput
                        {...inputProps}
                        icon={AccessTimeIcon}
                        label="Date"
                        allowEmpty
                      />
                    )}
                  />
                </Dialog>
              </form>
            );
          }}
        />
      )}
    </>
  );
};

export default ExportTopSpendersButton;
